import { useEffect, useEffectEvent } from "react";
import { useSearchParams } from "react-router";
import { GridLogicOperator } from "@mui/x-data-grid-premium";
import type { Form } from "../dynamicForm";
import { DynamicForm } from "../dynamicForm";
import { useGridContext, useInitialisedGridApiRef, WithInitialisedGridApiRef } from "./DynamicGrid";
import { HAS_OPTION_FILTER_OPERATOR } from "./hasOptionFilter";
import { selectDefaultSavedFilter, selectedSavedFilterId } from "./SavedFilters";

const OPTION_CATEGORY_PARAM = "option-category";
const OPTION_PARAM = "option";

export const optionFilterSearch = ({ categoryType, optionValue }: { categoryType: string; optionValue: string }) =>
  `?${new URLSearchParams({ [OPTION_CATEGORY_PARAM]: categoryType, [OPTION_PARAM]: optionValue }).toString()}`;

export const OptionFilterFromSearchParams = ({ form }: { form: Form }) => (
  <WithInitialisedGridApiRef>
    <ApplyOptionFilterFromSearchParams form={form} />
  </WithInitialisedGridApiRef>
);

const ApplyOptionFilterFromSearchParams = ({ form }: { form: Form }) => {
  const gridApiRef = useInitialisedGridApiRef();
  const { savedFiltersProps } = useGridContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryType = searchParams.get(OPTION_CATEGORY_PARAM);
  const optionValue = searchParams.get(OPTION_PARAM);
  const isSavedFilterSelected = selectedSavedFilterId(savedFiltersProps?.stringifiedFilters) !== null;

  const showDefaultSavedFilter = useEffectEvent(() =>
    savedFiltersProps?.onChange(selectDefaultSavedFilter(savedFiltersProps.stringifiedFilters))
  );

  const applyOptionFilter = useEffectEvent((categoryType: string, optionValue: string) => {
    const questionCardIds = questionCardIdsByCategoryType(form, categoryType);
    if (questionCardIds.length === 0) return;

    gridApiRef.current.setFilterModel({
      logicOperator: GridLogicOperator.Or,
      items: questionCardIds.map(questionCardId => ({
        id: questionCardId,
        field: questionCardId,
        operator: HAS_OPTION_FILTER_OPERATOR,
        value: optionValue,
      })),
    });

    setSearchParams(
      params => {
        params.delete(OPTION_CATEGORY_PARAM);
        params.delete(OPTION_PARAM);
        return params;
      },
      { replace: true }
    );
  });

  useEffect(() => {
    if (!categoryType || !optionValue) return;
    if (isSavedFilterSelected) return showDefaultSavedFilter();
    applyOptionFilter(categoryType, optionValue);
  }, [categoryType, optionValue, isSavedFilterSelected]);

  return null;
};

const questionCardIdsByCategoryType = (form: Form, categoryType: string) => {
  const questionCardIds: Array<string> = [];

  form.sections.forEach(({ questionCards }) =>
    questionCards.forEach(rootQuestionCard => {
      DynamicForm.traverseQuestionCard(rootQuestionCard, {
        onVisit: ({ id, input }) => {
          if (input.type === "dynamicMultipleChoiceInput" && input.categoryType === categoryType) {
            questionCardIds.push(id);
          }
        },
      });
    })
  );

  return questionCardIds;
};
